<template>
  <div class="tasks-page">
    <section class="hero">
      <div class="hero-content">
        <div class="hero-text">
          <h1>视频链接解析中心</h1>
          <p>粘贴短视频链接，立即启动爬虫下载与 AI 深度分析，快速生成可执行的洞察报告。</p>
          <div class="hero-actions">
            <button class="primary" @click="focusUrlInput">粘贴链接并解析</button>
            <router-link class="secondary" to="/search">返回视频搜索</router-link>
          </div>
          <div class="hero-badges">
            <span>极速解析</span>
            <span>多平台覆盖</span>
            <span>自动同步任务</span>
          </div>
        </div>
        <div class="hero-steps">
          <div class="step-card" v-for="(step, index) in workflowSteps" :key="step.title">
            <span class="step-index">{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="link-panel">
      <div class="panel-header">
        <div>
          <h2>解析视频链接</h2>
          <p>支持抖音、小红书、B站、YouTube、TikTok、快手等平台，自动识别视频信息。</p>
        </div>
        <div class="refresh-info">
          <span>每 {{ refreshIntervalLabel }} 自动刷新</span>
          <span v-if="lastRefreshed">上次同步：{{ lastRefreshed }}</span>
        </div>
      </div>

      <div class="link-form">
        <div class="input-wrapper">
          <label for="video-url">视频链接</label>
          <div class="input-field">
            <input
              id="video-url"
              ref="urlInput"
              v-model="videoUrl"
              type="url"
              placeholder="粘贴视频链接，例如 https://www.douyin.com/..."
              @keyup.enter="handleAnalyze"
            />
            <button class="paste-btn" type="button" @click="focusUrlInput">粘贴</button>
          </div>
        </div>

        <button
          class="analyze-button"
          :disabled="loading || !videoUrl.trim()"
          @click="handleAnalyze"
        >
          {{ loading ? '正在解析…' : '开始分析' }}
        </button>
      </div>

      <div class="platform-tags">
        <span class="tag" v-for="platform in supportedPlatforms" :key="platform">{{ platform }}</span>
      </div>

      <div class="platform-guides">
        <div v-for="guide in platformGuides" :key="guide.label" class="guide-item">
          <strong>{{ guide.label }}</strong>
          <span>{{ guide.tip }}</span>
        </div>
      </div>

      <div v-if="error" class="feedback error">{{ error }}</div>
      <div v-if="success" class="feedback success">{{ success }}</div>
    </section>

    <section class="task-stats">
      <div class="stats-card">
        <span class="label">运行中</span>
        <h3>{{ taskSummary.active }}</h3>
        <span class="hint">下载 / 解析任务</span>
      </div>
      <div class="stats-card">
        <span class="label">已完成</span>
        <h3>{{ taskSummary.completed }}</h3>
        <span class="hint">可查看分析报告</span>
      </div>
      <div class="stats-card">
        <span class="label">失败</span>
        <h3>{{ taskSummary.failed }}</h3>
        <span class="hint">可重试或检查链接</span>
      </div>
    </section>

    <section v-if="tasks.length" class="tasks-list">
      <div class="list-header">
        <h2>分析任务</h2>
        <span>实时同步最新状态</span>
      </div>

      <div class="tasks-grid">
        <article class="task-card" v-for="task in tasks" :key="task.task_id">
          <div class="card-header">
            <div>
              <h3>{{ task.video_meta.title || '未命名视频' }}</h3>
              <p class="platform">{{ getPlatformName(task.video_meta.platform) }}</p>
            </div>
            <span :class="['status-pill', task.status]">{{ getStatusText(task.status) }}</span>
          </div>

          <div class="card-meta">
            <div class="meta-item">
              <span class="label">作者</span>
              <span class="value">{{ task.video_meta.author || '未知' }}</span>
            </div>
            <div class="meta-item">
              <span class="label">时长</span>
              <span class="value">{{ formatDuration(task.video_meta.duration) }}</span>
            </div>
            <div class="meta-item">
              <span class="label">创建时间</span>
              <span class="value">{{ formatDate(task.created_at) }}</span>
            </div>
          </div>

          <div class="card-stats">
            <span>👁 {{ formatNumber(task.video_meta.view_count) }}</span>
            <span>❤️ {{ formatNumber(task.video_meta.like_count) }}</span>
            <span>💬 {{ formatNumber(task.video_meta.comment_count) }}</span>
          </div>

          <div v-if="task.video_meta.thumbnail_url" class="thumbnail">
            <img :src="task.video_meta.thumbnail_url" :alt="task.video_meta.title || '视频封面'" />
          </div>

          <div
            v-if="task.status === 'downloading' || task.status === 'processing' || task.status === 'analyzing'"
            class="progress"
          >
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: (task.progress || 0) + '%' }"></div>
            </div>
            <span class="progress-label">{{ task.progress ? task.progress + '%' : '处理中' }}</span>
          </div>

          <div class="card-actions">
            <a :href="resolveVideoUrl(task.video_meta)" target="_blank" rel="noopener" class="link">查看原视频</a>
            <router-link
              v-if="task.status === 'completed'"
              :to="`/analysis/${task.task_id}`"
              class="primary-action"
            >
              查看分析报告
            </router-link>
          </div>

          <div v-if="task.status === 'failed'" class="error-box">
            <strong>失败原因：</strong>
            <span>{{ task.error_message || '解析失败，请稍后重试。' }}</span>
          </div>
        </article>
      </div>
    </section>

    <section v-else class="empty-state">
      <div class="empty-card">
        <div class="icon">📋</div>
        <h3>尚无分析任务</h3>
        <p>粘贴一个视频链接，立即生成第一份分析报告。</p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { resolveApiUrl } from '@/utils/api'

interface VideoMeta {
  id: string
  platform: string
  title: string
  author: string
  description?: string
  duration: number
  video_url: string
  source_url?: string
  thumbnail_url?: string
  view_count?: number
  like_count?: number
  comment_count?: number
}

interface Task {
  task_id: string
  video_meta: VideoMeta
  status: 'pending' | 'downloading' | 'processing' | 'analyzing' | 'completed' | 'failed'
  progress?: number
  error_message?: string
  created_at: string
  completed_at?: string
}

const supportedPlatforms = ['抖音', '小红书', 'B站', 'YouTube', 'TikTok', '快手']
const platformGuides = [
  {
    label: 'YouTube',
    tip: '需在后端配置 YOUTUBE_COOKIES / YOUTUBE_PROXY（可扫码或手机验证码登录获取），以确保检索与下载成功。'
  },
  {
    label: 'TikTok',
    tip: '推荐配置 TIKTOK_COOKIES / TIKTOK_PROXY，建议使用官方客户端扫码或手机登录获取 Cookie。'
  }
]
const workflowSteps = [
  { title: '链接识别', desc: '自动判断平台类型，匹配专属爬虫策略。' },
  { title: '高速下载', desc: '调用多节点抓取视频及封面、互动数据。' },
  { title: 'AI 分析', desc: '结合模型分析剧情、场景、爆点与传播潜力。' }
]

const tasks = ref<Task[]>([])
const loading = ref(false)
const error = ref('')
const success = ref('')
const videoUrl = ref('')
const urlInput = ref<HTMLInputElement | null>(null)
const lastRefreshed = ref('')

const refreshIntervalMs = 3000
const refreshIntervalLabel = computed(() => `${refreshIntervalMs / 1000} 秒`)

const taskSummary = computed(() => {
  const summary = { active: 0, completed: 0, failed: 0 }
  for (const task of tasks.value) {
    if (task.status === 'completed') summary.completed += 1
    else if (task.status === 'failed') summary.failed += 1
    else summary.active += 1
  }
  return summary
})

const route = useRoute()

const loadTasks = async () => {
  try {
    const response = await fetch(resolveApiUrl('/api/video/tasks'))
    if (response.ok) {
      const data = await response.json()
      tasks.value = Array.isArray(data) ? data : []
      lastRefreshed.value = new Date().toLocaleTimeString('zh-CN')
    }
  } catch (fetchError) {
    console.error('Failed to load tasks:', fetchError)
  }
}

const handleAnalyze = async () => {
  if (!videoUrl.value.trim()) {
    error.value = '请输入视频链接'
    return
  }

  loading.value = true
  error.value = ''
  success.value = ''

  try {
    const response = await fetch(resolveApiUrl('/api/video/analyze'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `url=${encodeURIComponent(videoUrl.value)}`
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const detail = errorData?.detail

      let message = `分析失败: ${response.status} ${response.statusText}`
      if (typeof detail === 'string') {
        message = detail
      } else if (Array.isArray(detail)) {
        message = detail
          .map((item) => {
            if (!item) return ''
            if (typeof item === 'string') return item
            if (typeof item === 'object') {
              const msgParts = [item.msg, item.type, item.loc?.join?.('.')].filter(Boolean)
              return msgParts.length ? msgParts.join(' - ') : JSON.stringify(item)
            }
            return String(item)
          })
          .filter(Boolean)
          .join('；') || message
      } else if (detail && typeof detail === 'object') {
        message = detail.message || JSON.stringify(detail)
      }

      throw new Error(message)
    }

    await response.json()

    success.value = '视频分析已启动，请稍候查看进度。'
    videoUrl.value = ''

    await loadTasks()

  } catch (err) {
    error.value = err instanceof Error ? err.message : '分析失败'
    console.error('Analysis error:', err)
  } finally {
    loading.value = false
  }
}

const getStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    'pending': '等待中',
    'downloading': '下载中',
    'processing': '处理中',
    'analyzing': '分析中',
    'completed': '已完成',
    'failed': '失败'
  }
  return statusMap[status] || status
}

const getPlatformName = (platform: string): string => {
  const platformMap: Record<string, string> = {
    'douyin': '抖音',
    'xhs': '小红书',
    'bili': 'B站',
    'youtube': 'YouTube',
    'tiktok': 'TikTok',
    'ks': '快手'
  }
  return platformMap[platform] || platform
}

const resolveVideoUrl = (meta: VideoMeta) => meta.source_url || meta.video_url

const formatDuration = (seconds: number): string => {
  if (!seconds) return '未知'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const formatNumber = (value?: number) => {
  if (!value) return '0'
  if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
  if (value >= 10000) return (value / 10000).toFixed(1) + '万'
  return value.toLocaleString('zh-CN')
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN')
}

const focusUrlInput = async () => {
  urlInput.value?.focus()
  try {
    if (navigator.clipboard) {
      const text = await navigator.clipboard.readText()
      if (text && !videoUrl.value) {
        videoUrl.value = text.trim()
      }
    }
  } catch (clipboardError) {
    console.debug('Clipboard access denied:', clipboardError)
  }
}

let intervalId: number | undefined

onMounted(() => {
  const initialUrl = route.query.url
  if (typeof initialUrl === 'string' && initialUrl.trim()) {
    videoUrl.value = initialUrl
  }

  loadTasks()
  intervalId = window.setInterval(loadTasks, refreshIntervalMs)
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})

watch(
  () => route.query.url,
  (newUrl) => {
    if (typeof newUrl === 'string' && newUrl.trim()) {
      videoUrl.value = newUrl
      focusUrlInput()
    }
  }
)
</script>

<style scoped>
.tasks-page {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding-bottom: 4rem;
}

.hero {
  background: linear-gradient(120deg, #0f172a 0%, #4338ca 50%, #7c3aed 100%);
  border-radius: 0 0 48px 48px;
  padding: 4rem 3rem 5rem;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.2), transparent 55%),
    radial-gradient(circle at 85% 10%, rgba(255, 255, 255, 0.18), transparent 60%),
    radial-gradient(circle at 90% 75%, rgba(255, 255, 255, 0.15), transparent 65%);
  pointer-events: none;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  position: relative;
  z-index: 1;
}

.hero-text h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.hero-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.hero-actions .primary {
  background: #ffffff;
  color: #4338ca;
  border: none;
  padding: 0.9rem 1.6rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hero-actions .primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(67, 56, 202, 0.3);
}

.hero-actions .secondary {
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0.9rem 1.6rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.hero-actions .secondary:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: #ffffff;
}

.hero-badges {
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
  color: rgba(255, 255, 255, 0.85);
}

.hero-badges span {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.85rem;
}

.hero-steps {
  display: grid;
  gap: 1rem;
}

.step-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 1.6rem;
  backdrop-filter: blur(12px);
  display: grid;
  gap: 0.5rem;
}

.step-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  font-weight: 600;
}

.step-card h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.step-card p {
  margin: 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

.link-panel,
.task-stats,
.tasks-list,
.empty-state {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.link-panel {
  background: #ffffff;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  margin-top: -4rem;
  position: relative;
  z-index: 2;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.panel-header h2 {
  margin: 0 0 0.4rem;
  font-size: 1.75rem;
  color: #1f2937;
}

.panel-header p {
  margin: 0;
  color: #6b7280;
}

.refresh-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.9rem;
  color: #6366f1;
  text-align: right;
}

.link-form {
  display: grid;
  gap: 1.2rem;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.input-wrapper label {
  font-weight: 600;
  color: #1f2937;
}

.input-field {
  display: flex;
  gap: 0.6rem;
}

.input-field input {
  flex: 1;
  padding: 1rem 1.2rem;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-field input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  background: #ffffff;
}

.paste-btn {
  background: #eef2ff;
  color: #4338ca;
  border: none;
  padding: 0 1.3rem;
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
}

.analyze-button {
  background: linear-gradient(120deg, #4338ca, #7c3aed);
  color: #ffffff;
  border: none;
  padding: 1rem 1.6rem;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.analyze-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 18px 40px rgba(79, 70, 229, 0.25);
}

.analyze-button:disabled {
  background: #c7d2fe;
  cursor: not-allowed;
}

.platform-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.5rem;
}

.platform-tags .tag {
  background: #f3f4f6;
  color: #4338ca;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
}

.feedback {
  border-radius: 14px;
  padding: 1rem 1.2rem;
  margin-top: 1.5rem;
  font-size: 0.95rem;
}

.feedback.error {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.feedback.success {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.task-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.stats-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid #ede9fe;
  box-shadow: 0 10px 30px rgba(67, 56, 202, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  text-align: center;
}

.stats-card .label {
  font-size: 0.85rem;
  color: #6b7280;
}

.stats-card h3 {
  margin: 0;
  font-size: 2rem;
  color: #4338ca;
}

.stats-card .hint {
  font-size: 0.85rem;
  color: #9ca3af;
}

.tasks-list .list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.task-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 1.8rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.task-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 30px 60px rgba(67, 56, 202, 0.12);
  border-color: rgba(67, 56, 202, 0.2);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.8rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #1f2937;
  line-height: 1.4;
}

.card-header .platform {
  margin: 0.2rem 0 0;
  color: #6366f1;
  font-size: 0.9rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-pill.downloading,
.status-pill.processing,
.status-pill.analyzing {
  background: #dbeafe;
  color: #2563eb;
}

.status-pill.completed {
  background: #dcfce7;
  color: #16a34a;
}

.status-pill.failed {
  background: #fee2e2;
  color: #b91c1c;
}

.card-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.8rem;
}

.meta-item {
  background: #f9fafb;
  border-radius: 14px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.meta-item .label {
  font-size: 0.85rem;
  color: #6b7280;
}

.meta-item .value {
  font-size: 0.95rem;
  color: #1f2937;
  font-weight: 600;
  word-break: break-all;
}

.card-stats {
  display: flex;
  gap: 1.2rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.thumbnail {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.thumbnail img {
  width: 100%;
  display: block;
}

.progress {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.progress-bar {
  width: 100%;
  height: 10px;
  background: #e0e7ff;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(120deg, #4338ca, #7c3aed);
  transition: width 0.3s ease;
}

.progress-label {
  font-size: 0.85rem;
  color: #6366f1;
  font-weight: 600;
}

.card-actions {
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
}

.card-actions .link {
  color: #6366f1;
  font-weight: 600;
  text-decoration: none;
}

.card-actions .link:hover {
  text-decoration: underline;
}

.primary-action {
  background: linear-gradient(120deg, #4338ca, #7c3aed);
  color: #fff;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.primary-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(99, 102, 241, 0.25);
}

.error-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 14px;
  padding: 1rem;
  color: #b91c1c;
  font-size: 0.9rem;
}

.empty-state {
  display: flex;
  justify-content: center;
}

.empty-card {
  background: #fff;
  border-radius: 24px;
  padding: 3rem 2.5rem;
  text-align: center;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  border: 1px solid #ede9fe;
  display: grid;
  gap: 1rem;
}

.empty-card .icon {
  font-size: 2.5rem;
}

.empty-card h3 {
  margin: 0;
  font-size: 1.4rem;
  color: #1f2937;
}

.empty-card p {
  margin: 0;
  color: #6b7280;
  font-size: 0.95rem;
}

@media (max-width: 1024px) {
  .hero {
    padding: 3.2rem 2rem 4rem;
  }

  .link-panel {
    margin-top: -3rem;
    padding: 2rem;
  }
}

@media (max-width: 768px) {
  .hero {
    border-radius: 0 0 32px 32px;
  }

  .hero-text h1 {
    font-size: 2.3rem;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-actions .primary,
  .hero-actions .secondary {
    width: 100%;
    text-align: center;
  }

  .link-panel,
  .task-stats,
  .tasks-list,
  .empty-state {
    padding: 0 1rem;
  }

  .card-stats {
    flex-wrap: wrap;
    gap: 0.8rem;
  }
}

@media (max-width: 640px) {
  .panel-header {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .refresh-info {
    text-align: left;
  }

  .tasks-grid {
    grid-template-columns: 1fr;
  }
}
</style>
